<template>
  <template v-if="enabled">
    <pf-title h="3">{{ $t('Riscuoti il credito maturato di {0}', [formatCurrency(total, currency)]) }}</pf-title>
    <p>{{ $t( 'Allegaci la fattura che dovremo pagarti relativa al credito maturato, se necessario inserisci eventuali note e invia la richiesta. Il reparto amministrativo ti contatterà dandoti conferma una volta che la tua pratica verrà evasa.') }}</p>
  </template>
  <template v-else>
    <pf-title h="3">{{ $t('Credito maturato: {0}', [formatCurrency(total, currency)]) }}</pf-title>
    <p>{{ $t('Il credito potrà essere riscosso una volta maturati almeno {0}', [formatCurrency(collectFrom, currency)]) }}</p>
  </template>

  <pf-empty-state v-if="sending">
    <pf-empty-state-header :title="$t('Attendere. Invio in corso...')" heading-level="h4">
      <template #icon>
        <pf-empty-state-icon>
          <pf-spinner />
        </pf-empty-state-icon>
      </template>
    </pf-empty-state-header>
  </pf-empty-state>

  <pf-flex v-else>
    <pf-flex-item>
      <x-form
        :prevent-submit="!attachments.length"
        @submit="send"
        :submit-text="$t('Invia la richiesta')"
        style="max-width: 600px"
      >
        <form-group :name="$t('Allega la fattura')">
          <pf-button :disabled="!enabled" component="label" variant="control" style="cursor: pointer" small>
            <template #icon>
              <paperclip-icon />
            </template>
            {{ $t('Sfoglia') }}
            <input
              type="file"
              ref="inputAttachments"
              name="attachments"
              :disabled="!enabled"
              style="display: none"
              @change="refreshAttachments"
            >
          </pf-button>

          {{ ' ' }}

          <template v-if="attachments.length" v-for="(file, index) in attachments" :key="index">
            <pf-label
              @close="attachments.splice(index, 1)"
            >
              <template #icon>
                <paperclip-icon />
              </template>
              {{ file.name }} ({{ $size(file.size) }})
            </pf-label>
          </template>

          <pf-button v-else :disabled="!enabled" href="/rest/commissions_receipt" variant="link" small>
            {{ $t('Genera una ricevuta') }}
            <form-help-tooltip :text="$t('Clicca su questo link per generare una ricevuta completa di tutte le informazioni che ci occorrono per poterti effettuare il pagamento delle provvigioni. Controlla le informazioni e inviacela poi tramite questo form.')" />
          </pf-button>
        </form-group>

        <form-group
          :name="$t('Importo richiesto')"
          :helper-text="$t('Inserisci come importo l\'importo totale della fattura COMPRENSIVO DI IVA. Puoi richiedere da un minimo di {0} ad un massimo di {1}', [formatCurrency(collectFrom, currency), formatCurrency(total, currency)])"
          required
        >
          <x-input
            v-model="importo"
            name="importo"
            type="number"
            :min="collectFrom"
            :max="total"
            step="0.01"
            :disabled="!enabled"
            :placeholder="$t('Importo')"
            required
          />
        </form-group>

        <form-group :name="$t('IBAN')" required>
          <x-input
            v-model="newIban"
            name="iban"
            :disabled="!enabled"
            :placeholder="$t('IBAN per pagamento')"
            required
          />
        </form-group>

        <form-group :name="$t('Messaggio')">
          <pf-textarea
            v-model="message"
            :disabled="!enabled"
            :placeholder="$t('Aggiungi un messaggio alla richiesta')"
            rows="2"
          />
        </form-group>
      </x-form>
    </pf-flex-item>

    <pf-flex-item>
      <pf-title h="3">{{ $t("Dati per l'intestazione della fattura") }}</pf-title>
      <p>
        DHH Switzerland SA<br>
        Via Motta 18<br>
        CH-6830 {{ $t('Chiasso') }} – {{ $t('Svizzera') }}<br>
        {{ $t('N. IVA') }}: CHE-227.758.591<br>
      </p>
    </pf-flex-item>
  </pf-flex>
</template>

<script lang="ts" setup>
import { ref, type Ref } from 'vue';
import { useAlertStore } from '@/store/alerts';
import { $t as _t } from '@/i18n';
import { formatCurrency } from '@common/utils';
import { CreditDocumentsResource } from '@/resources';
import FormHelpTooltip from '@/components/FormHelpTooltip.vue';
import PaperclipIcon from '@vue-patternfly/icons/paperclip-icon';

const alert = useAlertStore();

const props = defineProps<{
  enabled: boolean;
  total: number;
  collectFrom: number;
  currency: 'CHF' | 'EUR';
  iban: string | null;
}>();

const emit = defineEmits<{
  (e: 'submit', event: Event|undefined): void
}>();

const sending = ref(false);
const message = ref('');
const importo = ref(props.total);
const newIban = ref(props.iban ?? '');
const attachments: Ref<File[]> = ref([]);
const inputAttachments: Ref<HTMLInputElement | undefined> = ref();

async function send(e: Event|undefined) {
  if (!inputAttachments?.value?.files || importo.value === null || newIban.value === null) {
    return;
  }

  sending.value = true;

  try {
    await new CreditDocumentsResource().save({
      tipo: 'provvigioni',
      importo: importo.value,
      iban: newIban.value,
      messaggio: message.value,
      allegati: inputAttachments?.value?.files,
    });

    importo.value = 0;
    message.value = '';
    attachments.value = [];
  } finally {
    sending.value = false;
  }

  alert.add({ title: _t('Fattura ricevuta correttamente e credito scalato. Verrai contattato dalla nostra amministrazione per il pagamento.'), type: 'success', toast: true});
  emit('submit', e);
};

function refreshAttachments() {
  if (!inputAttachments?.value?.files) {
    return;
  }

  for (const file of inputAttachments.value.files) {
    attachments.value.push(file);
  }
};
</script>
